import { create } from 'zustand';
import { useMyPrefsStore } from '@/lib/myPrefs';

/**
 * Adb device state strings come from `adb devices` output.
 * The most common ones are:
 *   - 'device'        : online & authorised
 *   - 'offline'       : known but currently disconnected
 *   - 'unauthorized'  : usb debugging dialog not yet accepted
 *   - 'unknown'       : we have a serial selection that the bridge has not seen yet
 */
export type DeviceConnState = 'device' | 'offline' | 'unauthorized' | 'unknown';

interface DeviceState {
  serial: string | undefined;
  currentState: DeviceConnState;
  /** Convenience: true only when state === 'device'. */
  isOnline: boolean;
  setSerial: (s: string | undefined) => void;
  setCurrentState: (state: DeviceConnState) => void;
}

const QUERY_KEY = 'serial';

/**
 * Initial serial source order:
 *   1. URL `?serial=` query (deep links / shared bookmarks).
 *   2. B1 prefs `ui.lastSerial` (persisted across browsers per account).
 *   3. undefined → user picks from the devices grid.
 *
 * Step 2 reads the prefs store synchronously at module init. The store
 * starts with defaults (`ui.lastSerial = null`) and gets overwritten
 * once `useMyPrefsStore.init()` (called by AuthGate) resolves; that
 * happens after the SPA mounts so this initial read just falls through
 * to step 3 on first load. No regression — the URL is the authoritative
 * source on a fresh visit anyway.
 */
function readInitial(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const params = new URLSearchParams(window.location.search);
  const fromQuery = params.get(QUERY_KEY);
  if (fromQuery) return fromQuery;
  const fromPrefs = useMyPrefsStore.getState().global.ui.lastSerial;
  return fromPrefs ?? undefined;
}

function syncUrl(s: string | undefined): void {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  if (s) url.searchParams.set(QUERY_KEY, s);
  else url.searchParams.delete(QUERY_KEY);
  window.history.replaceState({}, '', url.toString());
}

export const useDevice = create<DeviceState>((set) => ({
  serial: readInitial(),
  currentState: 'unknown',
  isOnline: false,
  setSerial: (s) =>
    set((cur) => {
      if (cur.serial === s) return cur;
      syncUrl(s);
      // Persist the selection to B1 prefs so refreshing on another browser
      // (or this one on a future visit) lands on the same device.
      useMyPrefsStore.getState().updateGlobal({ ui: { lastSerial: s ?? null } });
      return { serial: s };
    }),
  // Bail out when the value didn't actually change. Without this guard,
  // any `set` triggers a notify -> re-render even with identical values,
  // which combined with derived deps in subscribers (e.g. `devices` arrays
  // re-created each render) snowballs into "Maximum update depth exceeded"
  // (React error #185) → blank page.
  setCurrentState: (state) =>
    set((cur) => {
      const isOnline = state === 'device';
      if (cur.currentState === state && cur.isOnline === isOnline) return cur;
      return { currentState: state, isOnline };
    }),
}));

// Re-hydrate `serial` once prefs finish loading (URL didn't carry one but
// prefs has a remembered serial).
if (typeof window !== 'undefined') {
  const params = new URLSearchParams(window.location.search);
  if (!params.get(QUERY_KEY)) {
    useMyPrefsStore.subscribe((state, prev) => {
      if (state.ready && !prev.ready) {
        const remembered = state.global.ui.lastSerial;
        if (remembered && !useDevice.getState().serial) {
          useDevice.getState().setSerial(remembered);
        }
      }
    });
  }
}
